import { useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { supabase } from '@/lib/supabase'
import type { FaqItem, FaqSection } from '@limo/database'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { Loader2 } from 'lucide-react'

const faqItemSchema = z.object({
  section_id: z.string().uuid().nullable(),
  question: z.string().min(1, 'Question is required').max(500),
  answer: z.string().min(1, 'Answer is required').max(5000),
  display_order: z.coerce.number().int().min(0).default(0),
  is_active: z.boolean().default(true),
})

type FAQItemFormData = z.infer<typeof faqItemSchema>

interface FAQItemDialogProps {
  itemId: string | null
  accountId: string
  defaultSectionId?: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function FAQItemDialog({
  itemId,
  accountId,
  defaultSectionId,
  open,
  onOpenChange,
}: FAQItemDialogProps) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const isEditing = !!itemId

  const { data: item, isLoading: isLoadingItem } = useQuery({
    queryKey: ['faq-item', itemId],
    queryFn: async () => {
      if (!itemId) return null
      const { data, error } = await supabase
        .from('faq_items')
        .select('*')
        .eq('id', itemId)
        .single()
      if (error) throw error
      return data as FaqItem
    },
    enabled: !!itemId && open,
  })

  const { data: sections } = useQuery({
    queryKey: ['faq-sections-list', accountId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('faq_sections')
        .select('*')
        .eq('account_id', accountId)
        .order('display_order')
      if (error) throw error
      return data as FaqSection[]
    },
    enabled: open,
  })

  const form = useForm<FAQItemFormData>({
    resolver: zodResolver(faqItemSchema),
    defaultValues: {
      section_id: defaultSectionId ?? null,
      question: '',
      answer: '',
      display_order: 0,
      is_active: true,
    },
  })

  useEffect(() => {
    if (item) {
      form.reset({
        section_id: item.section_id,
        question: item.question,
        answer: item.answer,
        display_order: item.display_order,
        is_active: item.is_active,
      })
    } else if (!isEditing && open) {
      form.reset({
        section_id: defaultSectionId ?? null,
        question: '',
        answer: '',
        display_order: 0,
        is_active: true,
      })
    }
  }, [item, form, isEditing, open, defaultSectionId])

  const createMutation = useMutation({
    mutationFn: async (data: FAQItemFormData) => {
      const { error } = await supabase.from('faq_items').insert({
        ...data,
        account_id: accountId,
      })
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['faq-sections', accountId] })
      queryClient.invalidateQueries({ queryKey: ['faq-items-uncategorized', accountId] })
      toast({
        title: 'FAQ created',
        description: 'The FAQ has been successfully created.',
      })
      onOpenChange(false)
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  const updateMutation = useMutation({
    mutationFn: async (data: FAQItemFormData) => {
      if (!itemId) throw new Error('Item ID is required')
      const { error } = await supabase
        .from('faq_items')
        .update(data)
        .eq('id', itemId)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['faq-sections', accountId] })
      queryClient.invalidateQueries({ queryKey: ['faq-items-uncategorized', accountId] })
      queryClient.invalidateQueries({ queryKey: ['faq-item', itemId] })
      toast({
        title: 'FAQ updated',
        description: 'The FAQ has been successfully updated.',
      })
      onOpenChange(false)
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  const onSubmit = (data: FAQItemFormData) => {
    if (isEditing) {
      updateMutation.mutate(data)
    } else {
      createMutation.mutate(data)
    }
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      form.reset()
    }
    onOpenChange(open)
  }

  const isPending = createMutation.isPending || updateMutation.isPending

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit FAQ' : 'Add FAQ'}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update the FAQ question and answer.'
              : 'Add a new frequently asked question.'}
          </DialogDescription>
        </DialogHeader>

        {isEditing && isLoadingItem ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="section_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Section</FormLabel>
                    <Select
                      onValueChange={(value) =>
                        field.onChange(value === 'none' ? null : value)
                      }
                      value={field.value ?? 'none'}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a section" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">Uncategorized</SelectItem>
                        {sections?.map((section) => (
                          <SelectItem key={section.id} value={section.id}>
                            {section.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Assign this FAQ to a section
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="question"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Question</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="What are your hours of operation?"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="answer"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Answer</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="We are open Monday through Friday, 9am to 5pm..."
                        className="min-h-[150px]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="display_order"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Display Order</FormLabel>
                      <FormControl>
                        <Input type="number" min={0} {...field} />
                      </FormControl>
                      <FormDescription>Lower numbers first</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="is_active"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0 pt-8">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel>Active</FormLabel>
                      </div>
                    </FormItem>
                  )}
                />
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isPending}>
                  {isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {isEditing ? 'Update FAQ' : 'Add FAQ'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  )
}
